import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useFormik } from "formik";
import { useHistory } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";
import * as Yup from "yup";
import SkeletonLoaderBasic from "../../../loaders/SkeletonLoaderBasic";
import { handleUploadToS3BucketUrl } from "../../common/uploadTOS3BucketUrl";
import {
  GetPCProjectListFilters,
  GetPCTemplate,
  UpdatePCProject,
} from "../../../services/ProjectCenterServices";
import Button from "../../common/Button/Button";
import { GetListOfUserCancelToken } from "../../../services/UserService";
import FormLabel from "../../common/forms/FormLabel";
import FormInput from "../../common/forms/FormInput";
import { selectStyles as sharedSelectStyles } from "../../common/forms/selectStyles";
import PermissionsGate from "../../../utils/permissionGate";
import styled from "styled-components";
import {tokens} from "../../../public/tokens";
import {dateColorByMilestone, isValidUrl, emptyMilestone, emptyResource} from "../utils/ProjectCenterUtils";
import {STAGE_STATUS_OPTIONS, PROJECT_STATUS_OPTIONS} from "../utils/ProjectCenterConstants";
import HtmlInputEditor from "../../common/HtmlInputEditor";
import moment from "moment";
const dateToday = moment().format("YYYY-MM-DD");

const ResourceInputDiv = styled.div`
  display: flex;
  align-items: center;
  max-width: 70%;
`
const ResourceLinkText = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
const ResourcesAnchor = styled.a`
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
    max-width: 800px;
`

const validationSchema = Yup.object({
  name: Yup.string().required("Project Name is required").max(150, "Max 150 characters").trim(),
  department: Yup.string().nullable(),
  template_id: Yup.string().nullable(),
  status: Yup.string().required("Status is required"),
  accountable: Yup.string().nullable(),
  responsible: Yup.string().nullable(),
  start_date: Yup.string().nullable(),
  milestones: Yup.array()
    .of(
      Yup.object({
        name: Yup.string().required("Milestone name is required").trim(),
      })
    )
    .min(1, "At least one milestone is required"),
  resources: Yup.array().of(
    Yup.object({
      name: Yup.string().required("resource name is required").trim(),
    })
  ),
});
const MilestoneStatusDot = styled.span`
  background-color: ${({$color}) => $color};
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
  `
function PCProjectDetails({ id, projectDetails, loading, setLoading, setReload, reload }) {
  const history = useHistory();
  const [disabled, setDisabled] = useState(false);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [templateOptions, setTemplateOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [editingResourceUrls, setEditingResourceUrls] = useState({});
  const [description, setDescription] = useState("");

useEffect(() => {
  setDescription(projectDetails?.description || "");
}, [projectDetails]);

  const renderMilestoneStatusColorDot = (status) => {
    switch (status) {
      case "Not Started":
        return <MilestoneStatusDot $color={tokens.colors.states.alert[500]}></MilestoneStatusDot>;
      case "In Progress":
        return <MilestoneStatusDot $color={tokens.colors.brand["karoo-gold"][500]}></MilestoneStatusDot>;
      case "Review":
        return <MilestoneStatusDot $color={tokens.colors.brand["blue"][500]}></MilestoneStatusDot>;
      case "Complete":
        return <MilestoneStatusDot $color={tokens.colors.states.success[500]}></MilestoneStatusDot>;
      default:
        return <MilestoneStatusDot $color={tokens.colors.neutral.grey[500]}></MilestoneStatusDot>;
      }
  }
  // object storing if milestone with id is complete 
  const completedMilestones = useMemo(
    () => projectDetails?.milestones?.reduce((acc, milestone) => ({ ...acc, [milestone.id]: milestone.status === "Complete" }), {}),
    [projectDetails?.milestones]
  );

  const isResourceUrlEditing = (resource, index) =>
    editingResourceUrls[index] ?? !resource.url;

  const commitResourceUrl = (index) => {
    setEditingResourceUrls((prev) => ({ ...prev, [index]: false }));
  };

  const clearResourceUrl = (index) => {
    formik.setFieldValue(`resources[${index}].url`, "");
    setEditingResourceUrls((prev) => ({ ...prev, [index]: true }));
  };

  const handleResourceUpload = async (index, file) => {
    if (!file) return;

    try {
      const uploadPayload = [{
        file_name: `ProjectCenter/${projectDetails?.name || "Project"}/${file.name}_${moment.unix()}_${Math.random().toString(36).slice(2, 7)}`,
        file_type: file.type,
      }];
      const uploaded = await handleUploadToS3BucketUrl(uploadPayload, file);
      let responseUrl = uploaded?.[0]?.absolutePath;
      const parsedUrl = responseUrl ? responseUrl.split("?")[0] : "";
      if (uploaded?.length) {
        formik.setFieldValue(
          `resources[${index}].url`,
          parsedUrl
        );
        commitResourceUrl(index);
      }
    } catch (err) {
      console.error(err);

      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text: "Unable to upload the file.",
      });
    }
  };

  useEffect(() => {
    const dropdownSource = axios.CancelToken.source();
    const fetchData = async () => {
      try {
        const res = await GetListOfUserCancelToken({"viaStatus":["1"]}, dropdownSource.token);
        let x = { value: "", label: "" };
        x = res.data.map((data) => {
          return { ...x, value: `${data.UserID}`, label: `${data.Firstname + " " + data.Lastname}` };
        });
        setUserOptions(x);
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error(error);
        }
      }
    };
    fetchData();
    return () => {
      dropdownSource.cancel('Component unmounted');
    };
  }, []);
  useEffect(() => {
    const source = axios.CancelToken.source();

    const getFilters = async () => {
      try {
        const res = await GetPCProjectListFilters(source.token);
        if (res.status === 200) {
          setDepartmentOptions(res.data?.data?.DepartmentFilter || []);
          setTemplateOptions(res.data?.data?.TemplateFilter || []);
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error(error);
        }
      }
    };

    getFilters();

    return () => {
      source.cancel("Component unmounted");
    };
  }, []);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: projectDetails?.name || "",
      department: projectDetails?.department || "",
      template_id: projectDetails?.template_id || "",
      status: projectDetails?.status || "Active",
      accountable: projectDetails?.accountable || "",
      responsible: projectDetails?.responsible || "",
      start_date: projectDetails?.start_date
        ? projectDetails.start_date.split("T")[0]
        : "",
      watchers: projectDetails?.watchers?.length
        ? projectDetails.watchers.map((w) => w.UserID)
        : [],
      milestones: projectDetails?.milestones?.length
        ? projectDetails.milestones.map((milestone) => ({
          id: milestone.id,
          template_milestone_id: milestone.template_milestone_id,
          name: milestone.name,
          sequence: milestone.sequence,
          expected_date: milestone.expected_date
            ? milestone.expected_date.split("T")[0]
            : "",
          cardinal_date: milestone.cardinal_date
            ? milestone.cardinal_date.split("T")[0]
            : "",
          status: milestone.status || "Not Started",
        }))
        : [emptyMilestone()],
      resources: projectDetails?.resources?.length
        ? projectDetails.resources.map((l) => ({
          id: l.id,
          template_resource_id: l.template_resource_id,
          name: l.name,
          url: l.url || "",
          is_required: Boolean(l.is_required),
        }))
        : [],
    },
    validationSchema,
    onSubmit: async (values) => {
      setDisabled(true);

      const payload = {
        name: values.name,
        department: values.department || null,
        template_id: values.template_id || null,
        status: values.status,
        accountable: values.accountable || null,
        responsible: values.responsible || null,
        start_date: values.start_date || null,
        description: description || null,
        watchers: values.watchers,
        milestones: values.milestones.map((milestone, index) => ({
          id: milestone.id,
          template_milestone_id: milestone.template_milestone_id,
          name: milestone.name,
          sequence: index + 1,
          expected_date: milestone.expected_date || null,
          cardinal_date: milestone.cardinal_date || null,
          status: milestone.status,
        })),
        resources: values.resources.map((resource) => ({
          id: resource.id,
          template_resource_id: resource.template_resource_id,
          name: resource.name,
          url: resource.url,
          is_required: resource.is_required ? 1 : 0,
        })),
      };

      try {
        if (id > 0) {
          payload.id = id;
        }

        const result = await UpdatePCProject(payload);

        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Project created/updated successfully",
        });

        if (!Boolean(id)) {
          result?.data?.data?.id
            ? window.location.assign(`/project-center/projects/open/${result.data.data.id}`)
            : history.push(`/project-center/projects/table`);
        } else {
          setReload(!reload);
        }
      } catch (error) {
        console.error(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error?.response?.data?.message || "Something went wrong",
        });
      } finally {
        setDisabled(false);
      }
    },
  });

  useEffect(() => {
    if (!formik.values.template_id) return;

    const milestonesAreDefault =
      formik.values.milestones.length === 1 && !formik.values.milestones[0].name;
    const resourcesAreDefault = formik.values.resources.length === 0;

    if (!milestonesAreDefault && !resourcesAreDefault) return;

    const source = axios.CancelToken.source();

    const applyTemplateDefaults = async () => {
      try {
        const res = await GetPCTemplate(formik.values.template_id || id, source.token);
        if (res.status === 200) {
          if (milestonesAreDefault && res.data.data.milestones?.length) {
            formik.setFieldValue(
              "milestones",
              res.data.data.milestones.map((loadedMilestone) => ({
                id: null,
                template_milestone_id: loadedMilestone.id,
                name: loadedMilestone.name,
                sequence: loadedMilestone.sequence,
                expected_date: "",
                cardinal_date: "",
                status: "Not Started",
              }))
            );
          }

          if (resourcesAreDefault && res.data.data.resources?.length) {
            formik.setFieldValue(
              "resources",
              res.data.data.resources.map((loadedResource) => ({
                id: null,
                template_resource_id: loadedResource.id,
                name: loadedResource.name,
                url: "",
                is_required: Boolean(loadedResource.is_required),
              }))
            );
          }
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error(error);
        }
      }
    };

    applyTemplateDefaults();

    return () => {
      source.cancel("Component unmounted");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.template_id, id, projectDetails]);

  const addMilestone = () => {
    formik.setFieldValue("milestones", [...formik.values.milestones, emptyMilestone()]);
  };

  const removeMilestone = (index) => {
    const updated = formik.values.milestones.filter((_, i) => i !== index);
    formik.setFieldValue("milestones", updated.length ? updated : [emptyMilestone()]);
  };

  const moveMilestone = (index, direction) => {
    const updated = [...formik.values.milestones];
    const target = index + direction;
    if (target < 0 || target >= updated.length) return;
    [updated[index], updated[target]] = [updated[target], updated[index]];
    formik.setFieldValue("milestones", updated);
  };

  const addresource = () => {
    formik.setFieldValue("resources", [...formik.values.resources, emptyResource()]);
  };

  const removeresource = (index) => {
    const updated = formik.values.resources.filter((_, i) => i !== index);
    formik.setFieldValue("resources", updated);
  };

  if (loading) {
    return <SkeletonLoaderBasic />;
  }
  return (
    <div className="card card-profile-info-card filter-table-bg">
      <div className="new-card-header">
        <div className="card-header pt-0">
          {id > 0 ? "Edit Project" : "Add Project"}{" "}
          {disabled ? <i className="fal fa-cog fa-spin" /> : ""}
        </div>
        <div className="card-body-inr card-body-info">
          <form onSubmit={formik.handleSubmit}>
            <div className="row">
              <div className="col-md-6 col-lg-4">
                <div className="form-group mb-3">
                  <FormLabel required>Project Name</FormLabel>
                  <FormInput
                    {...formik.getFieldProps("name")}
                    error={formik.errors.name}
                    touched={formik.touched.name}
                    placeholder="Enter project name"
                  />
                </div>
              </div>

              <div className="col-md-6 col-lg-4">
                <div className="form-group mb-3">
                  <FormLabel>Department</FormLabel>
                  <Select
                    options={departmentOptions}
                    value={
                      departmentOptions.find(
                        (opt) => opt.value === formik.values.department
                      ) || null
                    }
                    onChange={(opt) =>
                      formik.setFieldValue("department", opt?.value || "")
                    }
                    placeholder="Select department..."
                    isClearable
                    menuPortalTarget={document.body}
                    styles={sharedSelectStyles}
                  />
                </div>
              </div>

              <div className="col-md-6 col-lg-4">
                <div className="form-group mb-3">
                  <FormLabel>Template</FormLabel>
                  <Select
                    options={templateOptions}
                    value={
                      templateOptions.find(
                        (opt) => opt.value === formik.values.template_id
                      ) || null
                    }
                    onChange={(opt) =>
                      formik.setFieldValue("template_id", opt?.value || "")
                    }
                    placeholder="Select template..."
                    isClearable
                    isDisabled={id > 0}
                    menuPortalTarget={document.body}
                    styles={sharedSelectStyles}
                  />
                  {id > 0 && (
                    <small className="text-muted">
                      Template can only be set when a project is created.
                    </small>
                  )}
                </div>
              </div>

              <div className="col-md-6 col-lg-4">
                <div className="form-group mb-3">
                  <FormLabel required>Status</FormLabel>
                  <Select
                    options={PROJECT_STATUS_OPTIONS}
                    value={
                      PROJECT_STATUS_OPTIONS.find(
                        (opt) => opt.value === formik.values.status
                      ) || null
                    }
                    onChange={(opt) =>
                      formik.setFieldValue("status", opt?.value || "")
                    }
                    placeholder="Select status..."
                    menuPortalTarget={document.body}
                    styles={sharedSelectStyles}
                  />
                  {formik.errors.status && (
                    <div className="invalid-feedback d-block">{formik.errors.status}</div>
                  )}
                </div>
              </div>
                <div className="col-md-6 col-lg-4">
                <div className="form-group mb-3">
                  <FormLabel>Start Date</FormLabel>
                  <FormInput
                    type="date"
                    name="start_date"
                    value={formik.values.start_date}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.errors.start_date}
                    touched={formik.touched.start_date}
                  />
                </div>
              </div>
              <div className="col-md-6 col-lg-4">
                <div className="form-group mb-3">
                  <FormLabel>Accountable <i className="fal fa-info-circle" title="The person who is responsible for the project" /></FormLabel>
                  <Select
                    options={userOptions}
                    value={
                      userOptions.find(
                        (opt) => Number(opt.value) === Number(formik.values.accountable)
                      ) || null
                    }
                    onChange={(opt) =>
                      formik.setFieldValue("accountable", opt?.value || "")
                    }
                    placeholder="Select accountable person..."
                    isClearable
                    menuPortalTarget={document.body}
                    styles={sharedSelectStyles}
                  />
                </div>
              </div>

              <div className="col-md-6 col-lg-4">
                <div className="form-group mb-3">
                  <FormLabel>Responsible <i className="fal fa-info-circle" title="The person who is currently working on the project" /></FormLabel>
                  <Select
                    options={userOptions}
                    value={
                      userOptions.find(
                        (opt) => Number(opt.value) === Number(formik.values.responsible)
                      ) || null
                    }
                    onChange={(opt) =>
                      formik.setFieldValue("responsible", opt?.value || "")
                    }
                    placeholder="Select responsible person..."
                    isClearable
                    menuPortalTarget={document.body}
                    styles={sharedSelectStyles}
                  />
                </div>
              </div>

              <div className="col-md-6 col-lg-8">
                <div className="form-group mb-3">
                  <FormLabel>Watchers <i className="fal fa-info-circle" title="Watchers are the people who are watching the project" /></FormLabel>
                  <Select
                    options={userOptions}
                    value={userOptions.filter((opt) =>
                      formik.values.watchers.includes(Number(opt.value))
                    )}
                    onChange={(opts) =>
                      formik.setFieldValue(
                        "watchers",
                        opts ? opts.map((o) => Number(o.value)) : []
                      )
                    }
                    placeholder="Select watchers..."
                    isMulti
                    isClearable
                    menuPortalTarget={document.body}
                    styles={sharedSelectStyles}
                  />
                </div>
              </div>
              <div className="col-12">
                <div className="form-group mb-3">
                  <FormLabel>Description</FormLabel>
                  <HtmlInputEditor
                    editorState={description}
                    setEditorState={setDescription}
                    hideSign={true}
                    belowNoMargin={true}
                    isCKEditor={true}
                    initialValue={projectDetails?.description}
                  />
                </div>
              </div>
            </div>

            <hr />

            <div className="row mb-2">
              <div className="col-12 d-flex align-items-center justify-content-between">
                <div className="card-header pt-0">Milestones</div>
                <Button
                  type="button"
                  title="Add Milestone"
                  onClick={addMilestone}
                  className="btn-save btn-success"
                >
                  <i className="fal fa-plus"></i> Add Milestone
                </Button>
              </div>
            </div>

            {typeof formik.errors.milestones === "string" && (
              <div className="invalid-feedback d-block mb-2">
                {formik.errors.milestones}
              </div>
            )}

            {formik.values.milestones.map((milestone, index) => (
              <div className="row align-items-center mb-3" key={index}>
                <div className="col-auto">
                    {renderMilestoneStatusColorDot(milestone.status)}
                </div>
                <div className="col-md-3">
                  <div className="form-group">
                    <FormLabel>Milestone</FormLabel>
                    <FormInput
                      name={`milestones[${index}].name`}
                      value={milestone.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.errors.milestones?.[index]?.name}
                      touched={formik.touched.milestones?.[index]?.name}
                      disabled={completedMilestones && completedMilestones[milestone.id] === true}
                      placeholder="Milestone name"
                    />
                  </div>
                </div>

                <div className="col-md-2">
                  <div className="form-group">
                    <FormLabel>Expected Date <i className="fal fa-info-circle" title="The date when the milestone is expected to be completed" /></FormLabel>
                    <FormInput
                      type="date"
                      name={`milestones[${index}].expected_date`}
                      value={milestone.expected_date}
                      onChange={formik.handleChange}
                      disabled={completedMilestones && completedMilestones[milestone.id] === true}
                      onBlur={formik.handleBlur}
                      style={{color: dateColorByMilestone(milestone.expected_date, milestone.status)}}
                    />
                  </div>
                </div>

                <div className="col-md-2">
                  <div className="form-group">
                    <FormLabel>Cardinal Date <i className="fal fa-info-circle" title="The date when the milestone has to be complete by" /></FormLabel>
                    <FormInput
                      type="date"
                      name={`milestones[${index}].cardinal_date`}
                      value={milestone.cardinal_date}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      style={{color: dateColorByMilestone(milestone.cardinal_date, milestone.status)}}
                      disabled={completedMilestones &&completedMilestones[milestone.id] === true}
                    />
                  </div>
                </div>

                <div className="col-md-2">
                  <div className="form-group">
                    <FormLabel>Status</FormLabel>
                    <Select
                      options={STAGE_STATUS_OPTIONS}
                      value={
                        STAGE_STATUS_OPTIONS.find(
                          (opt) => opt.value === milestone.status
                        ) || null
                      }
                      onChange={(opt) =>
                        formik.setFieldValue(
                          `milestones[${index}].status`,
                          opt?.value || "Not Started"
                        )
                      }
                      menuPortalTarget={document.body}
                      styles={sharedSelectStyles}
                    />
                  </div>
                </div>

                <div className="col-auto">
                  <button
                    type="button"
                    className="btn btn-primary rounded-circle"
                    title="Move Up"
                    disabled={index === 0}
                    onClick={() => moveMilestone(index, -1)}
                  >
                    <i className="fal fa-arrow-up"></i>
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary rounded-circle ml-1"
                    title="Move Down"
                    disabled={index === formik.values.milestones.length - 1}
                    onClick={() => moveMilestone(index, 1)}
                  >
                    <i className="fal fa-arrow-down"></i>
                  </button>

                  {!milestone.template_milestone_id && (
                    <button
                      type="button"
                      className="btn btn-danger rounded-circle ml-1"
                      title="Remove Milestone"
                      onClick={() => removeMilestone(index)}
                    >
                      <i className="fal fa-trash-alt"></i>
                    </button>
                  )}
                </div>
              </div>
            ))}

            <hr />

            <div className="row mb-2">
              <div className="col-12 d-flex align-items-center justify-content-between">
                <div className="card-header pt-0">resources</div>
                <Button
                  type="button"
                  title="Add resource"
                  onClick={addresource}
                  className="btn-save btn-success"
                >
                  <i className="fal fa-plus"></i> Add resource
                </Button>
              </div>
            </div>

            {formik.values.resources.map((resource, index) => (
              <div className="row align-items-center mb-2" key={index}>
                <div className="col-md-3">
                  <FormInput
                    name={`resources[${index}].name`}
                    value={resource.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.errors.resources?.[index]?.name}
                    touched={formik.touched.resources?.[index]?.name}
                    placeholder="resource name (e.g. Jira, Design)"
                  />
                </div>
                <ResourceInputDiv className="col">
                  {(resource.url && isValidUrl(resource.url) && !isResourceUrlEditing(resource, index)) ? (
                    <div className="d-flex align-items-center">
                      <ResourcesAnchor
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="as-text-blue curser flex-grow-1"
                        title={resource.url}
                      >
                        <ResourceLinkText>{resource.url}</ResourceLinkText>
                      </ResourcesAnchor>
                    </div>
                  ) : (
                    <div className="d-flex align-items-center flex-grow-1 me-4">
                      <FormInput
                        name={`resources[${index}].url`}
                        value={resource.url}
                        onChange={formik.handleChange}
                          onFocus={() =>
                            setEditingResourceUrls((prev) => ({ ...prev, [index]: true }))
                          }
                          onBlur={(e) => {
                            formik.handleBlur(e);
                            commitResourceUrl(index);
                          }}
                        placeholder="https://..."
                      />
                      <div className="flex-shrink-0 form-group atttach-file ml-2">
                        <label className="btn btn-outline-primary mb-0">
                          <span>Upload File</span>
                          <i className="fal fa-upload"></i>
                          <input
                            type="file"
                            hidden
                            className="form-control  form-control-aatch-file"
                            onChange={(e) => handleResourceUpload(index, e.target.files[0])}
                          />
                        </label>
                      </div>
                    </div>
                  )}
                </ResourceInputDiv>
                {(resource?.url && isValidUrl(resource.url)) &&
                  (
                    <div className="col-auto">
                    <button
                      type="button"
                      className="btn btn-danger rounded-circle"
                      title="Clear link"
                      onClick={() => clearResourceUrl(index)}
                    >
                      <i className="fal fa-times"></i>
                    </button>
                    </div>
                  )}
                {(!resource.is_required && !(resource.url && isValidUrl(resource.url)) )&& (
                  <div className="col-auto">
                    <button
                      type="button"
                      className="btn btn-danger rounded-circle"
                      title="Remove resource"
                      onClick={() => removeresource(index)}
                    >
                      <i className="fal fa-trash-alt"></i>
                    </button>
                  </div>
                )}
              </div>
            ))}

            <hr />

            <div className="form-group form-group-save-cancel mt-4">
              {id > 0 ? (
                <PermissionsGate
                  scopes={["projectcenterprojectsedit"]} errorProps={{ disabled: true }}
                >
                  <button
                    className="btn btn-save btn-success"
                    type="submit"
                    title="Save"
                    disabled={disabled}
                  >
                    {disabled ? (
                      <i className="fas fa-cog fa-spin"></i>
                    ) : (
                      <i className="fal fa-save"></i>
                    )}
                    Save
                  </button>
                </PermissionsGate>
              ) : (
                <PermissionsGate
                  scopes={["projectcenterprojectsadd"]} errorProps={{ disabled: true }}
                >
                <button
                  className="btn btn-save btn-success"
                  type="submit"
                  title="Save"
                  disabled={disabled}
                >
                  {disabled ? (
                    <i className="fal fa-cog fa-spin"></i>
                  ) : (
                    <i className="fal fa-save"></i>
                  )}
                  Create Project
                </button>
                </PermissionsGate>
              )}

              <button
                className="btn btn-close btn-danger"
                type="button"
                title="Cancel"
                onClick={() => {
                  formik.resetForm();
                }}
              >
                <i className="fal fa-times"></i>Cancel
              </button>

              {Object.keys(formik.values).map((key, ind) => {
                if (formik.touched[key] && formik.errors[key]) {
                  return (
                    <div className="invalid-feedback d-block" key={ind}>
                      {Array.isArray(formik.errors[key])
                        ? formik.errors[key]
                          .map((element) => element?.name || element)
                          .filter(Boolean)
                          .join(", ")
                        : formik.errors[key]}
                    </div>
                  );
                }
              })}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default PCProjectDetails;